import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button } from '../atoms';
import { DataTable } from './DataTable';
import { useToast } from './Toast';
import { formatDate } from '../lib/orgTimezone';

/**
 * LinkedDocumentsPanel — the documents FILED AGAINST one tax bill or one invoice,
 * shown on the money record's OWN screen (W0 · A0, criteria 3 + 4).
 *
 * WHY THIS EXISTS: a person looking at a bill wants the bill document next, and a
 * person looking at an invoice wants its supporting backup next — without first
 * navigating to a separate documents area and filtering back down to this record.
 *
 * Reads the Alliance-pilot linkage surface (#266 / migration 168), which was built
 * for exactly this panel (see the route comments in routes/document-links.js):
 *   bill    → GET /api/workflow/tax-bills/:id/documents   ("the bill screen's Documents panel")
 *   invoice → GET /api/workflow/invoices/:id/documents    ("the backup packet attached to one invoice")
 * Both endpoints book-scope the target record (assertOnBook), so an off-book bill
 * or invoice reads 404 exactly as its sibling handlers — the list cannot leak
 * another book's paperwork. Each row's bytes are ONE CLICK away via the shared
 * credentialed download (no navigation).
 *
 * Read-only by design: attaching a document to a bill/invoice is a workflow action
 * (document-links POST — for a bill it also marks a no_activity bill received), not
 * a plain file upload, so this surface lists + downloads and offers no upload
 * button. `documents.notify_client` remains the sole authority on portal visibility.
 */

interface LinkedDocument {
  id: string;
  name: string;
  document_type: string | null;
  filename: string | null;
  file_size_bytes: number | null;
  year: number | null;
  status: string | null;
  created_at: string | null;
}

export interface LinkedDocumentsPanelProps {
  /** The record the documents are filed against. Maps to the /api/workflow mount. */
  target: 'tax-bills' | 'invoices';
  targetId: string;
}

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function LinkedDocumentsPanel({ target, targetId }: LinkedDocumentsPanelProps) {
  const { toast } = useToast();

  const docsQuery = useQuery<LinkedDocument[]>({
    queryKey: ['linked-documents', target, targetId],
    queryFn: ({ signal }) => api.get<LinkedDocument[]>(`/api/workflow/${target}/${targetId}/documents`, signal),
    enabled: !!targetId,
  });

  const columns = useMemo<ColumnDef<LinkedDocument>[]>(() => [
    { accessorKey: 'name', header: 'Name', cell: ({ getValue, row }) => (getValue() as string) || row.original.filename || '—' },
    {
      accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => {
        const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—';
      },
    },
    { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'file_size_bytes', header: 'Size', cell: ({ getValue }) => fmtBytes(getValue() as number | null) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge> },
    { accessorKey: 'created_at', header: 'Filed', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    {
      id: 'download', header: '', cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m })); }}
        >Download</Button>
      ),
    },
  ], [toast]);

  const docs = docsQuery.data ?? [];

  if (docsQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading documents…</p>;
  }
  if (docsQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{(docsQuery.error as Error).message}</div>
    );
  }
  if (docs.length === 0) {
    return (
      <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        {target === 'invoices'
          ? 'No supporting documents attached to this invoice yet.'
          : 'No documents filed against this bill yet.'}
      </p>
    );
  }
  return <DataTable data={docs} columns={columns} />;
}
